/**
 * 🎨 LAYOUT GRID ELEMENT 2025
 * 
 * Grid Layout Node für infinite nesting
 * - Erstellt ein Grid mit konfigurierbaren Spalten
 * - Jede Cell kann wieder Content oder Layouts enthalten
 * - Backward compatible mit bestehendem System
 */

import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import GridFloatingToolbar from './GridFloatingToolbar';
import GridCellControls from './GridCellControls';
import AddElementMenu from './AddElementMenu';
import { applyGridPreset } from './gridPresets';
import { useDebouncedCallback } from '../../hooks/useDebounce';
import './LayoutGridElement.css';
import '../WidgetTypes/DeleteModal.css';

function LayoutGridElement({
    element,
    isEditMode,
    isMobileLayout,
    onUpdate,
    onDelete,
    onDuplicate, // ✅ NEU: Duplicate Grid Callback
    colors = {},
    colorAssignments = {},
    fonts = {},
    availableFonts = [],
    currentlyEditingElementId = null,
    onEditingChange = () => {},
    depth = 0, // ✅ NEU: Nesting depth (für Copy/Delete Button Visibility)
    // ✅ Recursive rendering function
    renderChild = null
}) {
    const [hoveredCell, setHoveredCell] = useState(null);
    const [activeCellMenu, setActiveCellMenu] = useState(null); // Which cell's menu is open
    const cellMenuRef = useRef(null);
    
    // 🗑️ Delete Cell Modal State
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [cellToDelete, setCellToDelete] = useState(null);
    const cellMenuButtonRef = useRef(null);
    const cellMenuCloseTimeoutRef = useRef(null);
    
    // Grid Hover State (für Add Column Button)
    const [isGridHovered, setIsGridHovered] = useState(false);
    
    // Grid Toolbar State
    const [isGridToolbarVisible, setIsGridToolbarVisible] = useState(false);
    const gridWrapperRef = useRef(null);
    
    // Close menu on outside click with DELAY
    useEffect(() => {
        const handleClickOutside = (event) => {
            const clickedInMenu = cellMenuRef.current && cellMenuRef.current.contains(event.target);
            const clickedInButton = cellMenuButtonRef.current && cellMenuButtonRef.current.contains(event.target);
            
            if (!clickedInMenu && !clickedInButton) {
                // ✅ Delay closing to prevent accidental closes
                cellMenuCloseTimeoutRef.current = setTimeout(() => {
                    setActiveCellMenu(null);
                }, 150);
            }
        };

        if (activeCellMenu !== null) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
                if (cellMenuCloseTimeoutRef.current) {
                    clearTimeout(cellMenuCloseTimeoutRef.current);
                }
            };
        }
    }, [activeCellMenu]);
    
    // Add content to cell
    const handleAddToCell = (cellId, contentType, elementType) => {
        const updatedChildren = element.children.map(cell => {
            if (cell.id === cellId) {
                // ✅ FIX: Ensure children array exists
                const currentChildren = cell.children || [];
                
                // ✅ Handle Grid Presets
                let newContent;
                if (contentType === 'gridPreset') {
                    newContent = applyGridPreset(elementType, currentChildren.length, colors, fonts, colorAssignments);
                    if (!newContent) return cell; // Fallback if preset doesn't exist
                } else {
                    newContent = createContentElement(contentType, elementType, currentChildren.length);
                }
                
                return {
                    ...cell,
                    children: [...currentChildren, newContent]
                };
            }
            return cell;
        });
        
        // ✅ Preserve ALL grid properties
        const updatedElement = {
            ...element,  // ✅ Alle bestehenden Properties behalten
            children: updatedChildren
        };
        
        console.log('➕ Add to Cell - Grid Properties preserved:', {
            gridDirection: updatedElement.gridDirection,
            gridDirectionMobile: updatedElement.gridDirectionMobile,
            gridFlowMode: updatedElement.gridFlowMode
        });
        
        // ✅ FIREBASE FIX: Remove all undefined values before updating
        const cleanElement = removeUndefinedValues(updatedElement);
        onUpdate(cleanElement);
        setActiveCellMenu(null);
    };
    
    // ✅ Helper: Remove undefined values (Firebase doesn't accept them)
    const removeUndefinedValues = (obj) => {
        if (Array.isArray(obj)) {
            return obj.map(item => removeUndefinedValues(item));
        }
        if (obj !== null && typeof obj === 'object') {
            return Object.entries(obj).reduce((acc, [key, value]) => {
                if (value !== undefined) {
                    acc[key] = removeUndefinedValues(value);
                }
                return acc;
            }, {});
        }
        return obj;
    };
    
    // ✅ NEW: Add new column to grid
    const handleAddColumn = () => {
        const currentColumns = element.children?.length || 0;
        // ✅ NEW: Use Grid's Cell Default Styles if available
        const newCell = {
            id: `cell-${Date.now()}`,
            type: 'cell',
            cellIndex: currentColumns,
            order: currentColumns,
            // ✅ Falls keine Cell Defaults im Grid → Fallback zu hardcoded Defaults
            // Cells ohne eigene Werte verwenden automatisch die Grid Defaults (siehe Rendering)
            children: [] // ✅ Always initialize children array
        };
        
        const updatedChildren = [...(element.children || []), newCell];
        
        // ✅ Preserve ALL grid properties (wichtig: gridDirection, gridFlowMode, etc. bleiben erhalten!)
        const updatedElement = {
            ...element,  // ✅ Alle bestehenden Properties behalten
            children: updatedChildren,
            columns: updatedChildren.length
        };
        
        console.log('➕ Add Column - Grid Properties preserved:', {
            gridDirection: updatedElement.gridDirection,
            gridDirectionMobile: updatedElement.gridDirectionMobile,
            gridFlowMode: updatedElement.gridFlowMode,
            columns: updatedElement.columns
        });
        
        // ✅ FIREBASE FIX: Remove undefined values
        const cleanElement = removeUndefinedValues(updatedElement);
        onUpdate(cleanElement);
    };
    
    // ✅ Delete cell from grid
    const handleDeleteCell = (cellId) => {
        if (element.children.length <= 1) {
            // ✅ Silent return - User kann via disabled button sehen dass nicht löschbar
            return;
        }
        
        const updatedChildren = element.children.filter(cell => cell.id !== cellId);
        
        // ✅ Preserve ALL grid properties (wichtig: gridDirection, gridFlowMode, etc. bleiben erhalten!)
        const updatedElement = {
            ...element,  // ✅ Alle bestehenden Properties behalten
            children: updatedChildren,
            columns: updatedChildren.length
        };
        
        console.log('🗑️ Delete Cell - BEFORE clean:', {
            gridDirection: updatedElement.gridDirection,
            gridDirectionMobile: updatedElement.gridDirectionMobile,
            gridFlowMode: updatedElement.gridFlowMode,
            columns: updatedElement.columns,
            allKeys: Object.keys(updatedElement)
        });
        
        // ✅ FIREBASE FIX: Remove undefined values
        const cleanElement = removeUndefinedValues(updatedElement);
        
        console.log('🗑️ Delete Cell - AFTER clean:', {
            gridDirection: cleanElement.gridDirection,
            gridDirectionMobile: cleanElement.gridDirectionMobile,
            gridFlowMode: cleanElement.gridFlowMode,
            columns: cleanElement.columns,
            allKeys: Object.keys(cleanElement)
        });
        
        onUpdate(cleanElement);
    };
    
    // ✅ Copy cell in grid
    const handleCopyCell = (cellId) => {
        const cellToCopy = element.children.find(cell => cell.id === cellId);
        if (!cellToCopy) return;
        
        // ✅ Deep clone der Cell mit neuem ID
        const clonedCell = {
            ...cellToCopy,
            id: `cell-${Date.now()}`,
            cellIndex: element.children.length,
            order: element.children.length,
            // ✅ Deep clone children mit neuen IDs
            children: (cellToCopy.children || []).map((child, idx) => ({
                ...child,
                id: `${child.type}-${Date.now()}-${idx}`,
                order: idx
            }))
        };
        
        const updatedChildren = [...element.children, clonedCell];
        
        // ✅ Preserve ALL grid properties (wichtig: gridDirection, gridFlowMode, etc. bleiben erhalten!)
        const updatedElement = {
            ...element,  // ✅ Alle bestehenden Properties behalten
            children: updatedChildren,
            columns: updatedChildren.length
        };
        
        console.log('📋 Copy Cell - Grid Properties preserved:', {
            gridDirection: updatedElement.gridDirection,
            gridDirectionMobile: updatedElement.gridDirectionMobile,
            gridFlowMode: updatedElement.gridFlowMode,
            columns: updatedElement.columns
        });
        
        // ✅ FIREBASE FIX: Remove undefined values
        const cleanElement = removeUndefinedValues(updatedElement);
        onUpdate(cleanElement);
    };
    
    // Create content element (same logic as DynamicContainerSection)
    const createContentElement = (type, elementType, order) => {
        const baseElement = {
            id: `${type}-${Date.now()}`,
            type: type,
            order: order,
            visible: true,
            elementType: type === 'button' ? 'button' : elementType
        };
        
        // Add type-specific properties
        if (type === 'text') {
            const textDefaults = {
                'h1': { fontSize: 48, fontSizeMobile: 36, fontWeight: '700', content: 'New Heading' },
                'h2': { fontSize: 36, fontSizeMobile: 28, fontWeight: '600', content: 'New Subheading' },
                'h3': { fontSize: 24, fontSizeMobile: 20, fontWeight: '600', content: 'New Subtitle' },
                'p': { fontSize: 16, fontSizeMobile: 14, fontWeight: '400', content: 'New paragraph...' }
            };
            
            // ✅ FIXED: Nutze Brand Font basierend auf elementType (H1 = Big Font, Rest = Small Font)
            const defaultFont = elementType === 'h1' 
                ? (fonts.globalBigFont || '') 
                : (fonts.globalSmallFont || '');
                
            // ✅ FIXED: Nutze Brand Text Color basierend auf colorAssignments
            const defaultColor = (() => {
                if (elementType === 'h1' || elementType === 'h2' || elementType === 'h3') {
                    const colorKey = colorAssignments.primaryText || 'color2';
                    return colors[colorKey] || '#333333';
                } else {
                    const colorKey = colorAssignments.secondaryText || 'color3';
                    return colors[colorKey] || '#666666';
                }
            })();
            
            return {
                ...baseElement,
                ...(textDefaults[elementType] || textDefaults['p']),
                fontFamily: defaultFont,
                color: defaultColor,
                textAlign: 'center', // ✅ DEFAULT: Mittig
                textAlignMobile: 'center', // ✅ DEFAULT: Mittig auf Mobile
                marginTop: 0,
                marginBottom: 0
            };
        }
        
        if (type === 'button') {
            // ✅ FIXED: Nutze Brand Button Colors basierend auf colorAssignments
            const buttonBgColor = colors[colorAssignments.buttons || 'color1'] || '#5C80BC';
            const buttonTxtColor = colors[colorAssignments.buttonText || 'color5'] || '#FFFFFF';
            const buttonFont = fonts.globalSmallFont || '';
            
            return {
                ...baseElement,
                buttonText: 'Click me',
                buttonTextSize: 18,
                buttonTextSizeMobile: 16,
                buttonWidth: 200, // ✅ Desktop: 200px
                buttonWidthMobile: 100, // ✅ Mobile: 100% (Prozent-Wert)
                buttonMaxWidth: 300,
                buttonMaxWidthMobile: 280,
                buttonHeight: 50,
                buttonHeightMobile: 48,
                buttonBorderRadius: 8,
                buttonBorderRadiusMobile: 8,
                buttonColor: buttonBgColor,
                buttonTextColor: buttonTxtColor,
                buttonFontFamily: buttonFont,
                buttonAlignment: 'center', // ✅ DEFAULT: Mittig
                buttonAlignmentMobile: 'center', // ✅ DEFAULT: Mittig auf Mobile
                marginTop: 0,
                marginBottom: 0
            };
        }
        
        if (type === 'image') {
            return {
                ...baseElement,
                imageUrl: '',
                width: 100,
                height: 300,
                widthType: 'percent',
                heightType: 'auto',
                borderRadius: 8,
                objectFit: 'cover'
            };
        }
        
        if (type === 'video') {
            return {
                ...baseElement,
                videoUrl: '',
                videoWidth: 100,
                videoWidthMobile: 100,
                videoWidthType: '%',
                videoHeight: 500,
                videoHeightMobile: 400,
                aspectRatio: '16-9',
                videoAlignment: 'center',
                borderRadius: 12,
                borderRadiusMobile: 12,
                borderEnabled: false,
                borderWidth: 2,
                borderColor: '#667eea',
                shadowEnabled: false,
                shadowBlur: 16,
                shadowDistance: 8,
                shadowOpacity: 25,
                marginTop: 20,
                marginBottom: 20,
                marginLeft: 0,
                marginRight: 0
            };
        }
        
        if (type === 'divider') {
            return {
                ...baseElement,
                dividerStyle: 'simple-line',
                dividerColor: colors.accentColor || '#5C80BC',
                dividerWidth: 100,
                marginTop: 20,
                marginBottom: 20
            };
        }
        
        // ✅ INFINITE NESTING: Grid in Grid
        if (type === 'grid') {
            const cols = elementType === '2-columns' ? 2 : (elementType === '3-columns' ? 3 : 2);
            return {
                ...baseElement,
                columns: cols,
                gap: 20,
                gapMobile: 15,
                marginTop: 20,
                marginBottom: 20,
                // ✅ Max Width (Desktop/Mobile) - Zentriert das Grid
                maxWidth: 1200,
                maxWidthMobile: 900,
                // ✅ Grid Direction & Flow (Desktop)
                gridDirection: 'horizontal',
                gridFlowMode: 'fixed',
                maxItemsPerRow: 3,
                minItemWidth: 200,
                columnTemplate: 'equal',
                // ✅ Grid Direction & Flow (Mobile)
                gridDirectionMobile: 'horizontal',
                gridFlowModeMobile: 'fixed',
                maxItemsPerRowMobile: 3,
                minItemWidthMobile: 200,
                columnTemplateMobile: 'equal',
                // ✅ Cell Defaults (können in Cell Styling Section geändert werden)
                cellDefaultBackground: 'transparent',
                cellDefaultGradientEnabled: false,
                cellDefaultGradientColor1: '#667eea',
                cellDefaultGradientColor2: '#764ba2',
                cellDefaultOpacity: 100,
                cellDefaultBackdropBlur: 'none',
                cellDefaultBoxShadow: 'none',
                cellDefaultPadding: 12,
                cellDefaultPaddingMobile: 10,
                cellDefaultMinWidth: 200,
                cellDefaultMinWidthMobile: 150,
                cellDefaultMaxWidth: 800,
                cellDefaultMaxWidthMobile: 600,
                cellDefaultBorderRadius: 6,
                cellDefaultBorderEnabled: false,
                cellDefaultBorderWidth: 1,
                cellDefaultBorderColor: '#e0e0e0',
                cellDefaultFixedHeight: false,
                cellDefaultHeight: 200,
                // ✅ Cell Layout & Alignment Defaults
                cellDefaultFlexDirection: 'column',
                cellDefaultFlexDirectionMobile: 'column',
                cellDefaultAlignItems: 'flex-start',
                cellDefaultJustifyContent: 'center',
                // ✅ Cells ohne eigene Werte → Verwenden Grid Defaults
                children: Array.from({ length: cols }, (_, i) => ({
                    id: `cell-${Date.now()}-${i}`,
                    type: 'cell',
                    cellIndex: i,
                    order: i,
                    children: []
                }))
            };
        }
        
        return baseElement;
    };
    
    // ⚡ OPTIMISTIC UI: Lokaler State für Slider-Werte (SOFORTIGES Feedback)
    // ✅ MUSS ZUERST deklariert werden (wird unten genutzt!)
    const [localOptimisticStyles, setLocalOptimisticStyles] = useState({
        maxWidth: element.maxWidth,
        maxWidthMobile: element.maxWidthMobile,
        marginTop: element.marginTop,
        marginBottom: element.marginBottom,
        marginLeft: element.marginLeft,
        marginRight: element.marginRight,
        marginTopMobile: element.marginTopMobile,
        marginBottomMobile: element.marginBottomMobile,
        marginLeftMobile: element.marginLeftMobile,
        marginRightMobile: element.marginRightMobile,
        // ✅ Padding für Grid Wrapper
        padding: element.padding,
        paddingMobile: element.paddingMobile,
        paddingTop: element.paddingTop,
        paddingRight: element.paddingRight,
        paddingBottom: element.paddingBottom,
        paddingLeft: element.paddingLeft,
        paddingTopMobile: element.paddingTopMobile,
        paddingRightMobile: element.paddingRightMobile,
        paddingBottomMobile: element.paddingBottomMobile,
        paddingLeftMobile: element.paddingLeftMobile,
        gap: element.gap,
        gapMobile: element.gapMobile,
        borderRadius: element.borderRadius,
        cellDefaultOpacity: element.cellDefaultOpacity,
        cellDefaultBorderRadius: element.cellDefaultBorderRadius,
        cellDefaultBorderWidth: element.cellDefaultBorderWidth,
        cellDefaultHeight: element.cellDefaultHeight,
        cellDefaultPadding: element.cellDefaultPadding,
        cellDefaultPaddingMobile: element.cellDefaultPaddingMobile
    });
    
    // ⚡ NEUE STRATEGIE: KEIN automatischer Sync mit element!
    // → localOptimisticStyles ist die "single source of truth" während editing
    // → Wird NUR von GridFloatingToolbar updated (via onStyleChange)
    // → Firebase Updates triggern KEINE Re-Syncs (verhindert Springen!)
    // 
    // ✅ Initial Sync passiert nur beim Mount (useState initial value)
    // ✅ Danach: NUR Toolbar Updates zählen
    // ✅ Nach Page Reload: Neue Mount → neue Initial Values → korrekt!
    
    // ⚡ Debounced Parent Update (300ms für Firebase - wie UniversalTextElement)
    const debouncedParentUpdate = useDebouncedCallback((updatedElement) => {
        if (onUpdate) {
            console.log('💾 [DEBOUNCED] Saving grid updates to Firebase...');
            onUpdate(updatedElement);
        }
    }, 300);
    
    // Grid Properties mit Defaults (responsive)
    const columns = element.columns || 2;
    
    // ✅ Responsive Grid Settings (mit optimistic fallback)
    const columnTemplate = isMobileLayout
        ? (element.columnTemplateMobile ?? element.columnTemplate ?? 'equal')
        : (element.columnTemplate ?? 'equal');
    
    const gridDirection = isMobileLayout
        ? (element.gridDirectionMobile ?? element.gridDirection ?? 'horizontal')
        : (element.gridDirection ?? 'horizontal');
    
    const gridFlowMode = isMobileLayout
        ? (element.gridFlowModeMobile ?? element.gridFlowMode ?? 'fixed')
        : (element.gridFlowMode ?? 'fixed');
    
    const maxItemsPerRow = isMobileLayout
        ? (element.maxItemsPerRowMobile ?? element.maxItemsPerRow ?? 3)
        : (element.maxItemsPerRow ?? 3);
    
    const minItemWidth = isMobileLayout
        ? (element.minItemWidthMobile ?? element.minItemWidth ?? 200)
        : (element.minItemWidth ?? 200);
    
    // ⚡ OPTIMISTIC: Gap NUR aus localOptimisticStyles - KEINE element Fallbacks!
    const gap = isMobileLayout
        ? (localOptimisticStyles.gapMobile ?? localOptimisticStyles.gap ?? 20)
        : (localOptimisticStyles.gap ?? 20);
    
    // ✅ Generate grid-template-columns based on template, flow mode AND direction
    const getGridTemplateColumns = () => {
        // ✅ VERTICAL DIRECTION: Immer nur eine Spalte (alles untereinander)!
        if (gridDirection === 'vertical' && gridFlowMode === 'fixed') {
            return '1fr'; // ✅ Eine Spalte = alles untereinander
        }
        
        // Auto-Fit Mode: Responsive wrapping
        if (gridFlowMode === 'auto-fit') {
            // Nutze responsive Werte
            const minWidth = minItemWidth;
            const maxItems = maxItemsPerRow;
            
            // ✅ FIX: Bei horizontal direction = Items nebeneinander (columns)
            // Nutze repeat(maxItems, 1fr) statt auto-fit für predictable Layout
            if (gridDirection === 'horizontal') {
                return `repeat(${maxItems}, 1fr)`;
            } else {
                // Bei vertical direction = eine Spalte, Items untereinander (rows)
                return '1fr';
            }
        }
        
        // ✅ Get Column Min-Widths (responsive)
        const columnMinWidths = isMobileLayout
            ? (element.columnMinWidthsMobile ?? element.columnMinWidths ?? [])
            : (element.columnMinWidths ?? []);
        
        // ✅ Helper: Apply min-width to fr value
        const applyMinWidth = (frValue, colIndex) => {
            const minWidth = columnMinWidths[colIndex];
            if (minWidth && minWidth > 0) {
                return `minmax(${minWidth}px, ${frValue})`;
            }
            return frValue;
        };
        
        // Fixed Mode: Traditional templates
        switch (columnTemplate) {
            case 'equal':
                // ✅ Apply min-widths if set
                if (columnMinWidths.some(w => w && w > 0)) {
                    return Array(columns).fill(0).map((_, i) => applyMinWidth('1fr', i)).join(' ');
                }
                return `repeat(${columns}, 1fr)`;
            case '1-2':
                return `${applyMinWidth('1fr', 0)} ${applyMinWidth('2fr', 1)}`;
            case '2-1':
                return `${applyMinWidth('2fr', 0)} ${applyMinWidth('1fr', 1)}`;
            case '1-3':
                return `${applyMinWidth('1fr', 0)} ${applyMinWidth('3fr', 1)}`;
            case '3-1':
                return `${applyMinWidth('3fr', 0)} ${applyMinWidth('1fr', 1)}`;
            case '1-1-1':
                return `${applyMinWidth('1fr', 0)} ${applyMinWidth('1fr', 1)} ${applyMinWidth('1fr', 2)}`;
            case '1-2-1':
                return `${applyMinWidth('1fr', 0)} ${applyMinWidth('2fr', 1)} ${applyMinWidth('1fr', 2)}`;
            case '2-1-1':
                return `${applyMinWidth('2fr', 0)} ${applyMinWidth('1fr', 1)} ${applyMinWidth('1fr', 2)}`;
            // ✨ Content-based width templates (perfekt für Image + Text)
            case 'auto-rest':
                // Cell 1: Auto-width (passt sich Content an), Cell 2: Rest
                // ✅ Min-width für rest column anwendbar
                return `auto ${applyMinWidth('1fr', 1)}`;
            case 'min-rest':
                // Cell 1: Minimal width (so klein wie möglich), Cell 2: Rest
                return `min-content ${applyMinWidth('1fr', 1)}`;
            case 'fit-rest':
                // Cell 1: Fit-content (passt sich an aber max 300px), Cell 2: Rest
                const fitMaxWidth = columnMinWidths[0] || 300;
                return `fit-content(${fitMaxWidth}px) ${applyMinWidth('1fr', 1)}`;
            case 'auto-auto':
                // Beide Cells: Auto-width (passen sich beide Content an)
                return 'auto auto';
            case 'auto-auto-rest':
                // 3 Cells: Cell 1 + 2 Auto, Cell 3 Rest (für Checkbox + Icon + Text)
                return `auto auto ${applyMinWidth('1fr', 2)}`;
            case 'auto-auto-auto-auto':
                // 4 Cells: Alle Auto-width
                return 'auto auto auto auto';
            case 'custom':
                return element.customWidths?.join(' ') || `repeat(${columns}, 1fr)`;
            default:
                return `repeat(${columns}, 1fr)`;
        }
    };
    
    // ✅ Generate grid-template-rows for vertical direction
    const getGridTemplateRows = () => {
        // ✅ VERTICAL DIRECTION: gridTemplateRows ist nicht relevant
        // Da gridAutoFlow: 'row' und gridTemplateColumns: '1fr', fließen Items automatisch nach unten
        if (gridDirection === 'vertical' && gridFlowMode === 'fixed') {
            return 'auto'; // ✅ Auto rows - jede Row passt sich Content an
        }
        
        if (gridFlowMode === 'auto-fit') {
            const maxItems = maxItemsPerRow;
            
            // Bei auto-fit mit horizontal direction
            if (gridDirection === 'horizontal') {
                return 'auto'; // Auto rows (wrapped automatisch)
            } else {
                // Bei vertical direction
                return 'auto'; // Auto rows
            }
        }
        
        // Default: auto rows
        return 'auto';
    };
    
    // Children (Cells)
    const children = element.children || [];
    
    const [showAddColumn, setShowAddColumn] = useState(false);
    const hideColumnButtonTimeout = useRef(null);
    
    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (hideColumnButtonTimeout.current) {
                clearTimeout(hideColumnButtonTimeout.current);
            }
        };
    }, []);
    
    // ✅ Box Shadow Helper
    const getBoxShadowValue = (shadow) => {
        const shadowMap = {
            'small': '0 2px 4px rgba(0, 0, 0, 0.1)',
            'medium': '0 4px 12px rgba(0, 0, 0, 0.15)',
            'large': '0 8px 24px rgba(0, 0, 0, 0.2)',
            'none': 'none'
        };
        return shadowMap[shadow] || 'none';
    };
    
    // Wrapper Styles (Border, Background, Shadow)
    const isSeamlessMode = element.seamlessMode || false;
    
    // ⚡ OPTIMISTIC: Border Radius aus localOptimisticStyles
    // ⚡ OPTIMISTIC: NUR localOptimisticStyles - KEIN element Fallback!
    const activeBorderRadius = localOptimisticStyles.borderRadius ?? 0;
    
    const wrapperStyles = {
        border: (element.borderEnabled || isSeamlessMode)
            ? `${element.borderWidth || 1}px solid ${element.borderColor || '#e0e0e0'}`
            : 'none',
        borderRadius: isSeamlessMode ? 0 : `${activeBorderRadius}px`, // ✅ Optimistic border radius
        background: element.backgroundColor || 'transparent',
        boxShadow: getBoxShadowValue(element.boxShadow),  // ✅ Grid Shadow
        padding: element.borderEnabled || element.backgroundColor || isSeamlessMode ? '0' : '0',
        overflow: isSeamlessMode ? 'hidden' : 'visible', // ✅ Clip borders at edges
        width: '100%', // ✅ Full width
        // ❌ ENTFERNT: marginLeft/Right 'auto' - wird via useEffect gesetzt!
        boxSizing: 'border-box', // ✅ Include border in width calculation
    };
    
    // ✅ Apply maxWidth + Margins + Padding via useEffect (SOFORT via DOM für optimistic UI)
    useEffect(() => {
        if (gridWrapperRef.current) {
            // ✅ NUR localOptimisticStyles - KEINE element Fallbacks!
            // → Verhindert dass alte element Werte das UI überschreiben
            const maxWidth = isMobileLayout
                ? `${localOptimisticStyles.maxWidthMobile ?? localOptimisticStyles.maxWidth ?? 1200}px`
                : `${localOptimisticStyles.maxWidth ?? 1200}px`;
            
            const marginTop = isMobileLayout
                ? `${localOptimisticStyles.marginTopMobile ?? localOptimisticStyles.marginTop ?? 0}px`
                : `${localOptimisticStyles.marginTop ?? 0}px`;
            
            const marginBottom = isMobileLayout
                ? `${localOptimisticStyles.marginBottomMobile ?? localOptimisticStyles.marginBottom ?? 0}px`
                : `${localOptimisticStyles.marginBottom ?? 0}px`;
            
            const marginLeft = isMobileLayout
                ? `${localOptimisticStyles.marginLeftMobile ?? localOptimisticStyles.marginLeft ?? 0}px`
                : `${localOptimisticStyles.marginLeft ?? 0}px`;
            
            const marginRight = isMobileLayout
                ? `${localOptimisticStyles.marginRightMobile ?? localOptimisticStyles.marginRight ?? 0}px`
                : `${localOptimisticStyles.marginRight ?? 0}px`;
            
            // ✅ Padding (unterstützt unified oder separate values)
            const paddingTop = isMobileLayout
                ? `${localOptimisticStyles.paddingTopMobile ?? localOptimisticStyles.paddingMobile ?? localOptimisticStyles.paddingTop ?? localOptimisticStyles.padding ?? 0}px`
                : `${localOptimisticStyles.paddingTop ?? localOptimisticStyles.padding ?? 0}px`;
            
            const paddingRight = isMobileLayout
                ? `${localOptimisticStyles.paddingRightMobile ?? localOptimisticStyles.paddingMobile ?? localOptimisticStyles.paddingRight ?? localOptimisticStyles.padding ?? 0}px`
                : `${localOptimisticStyles.paddingRight ?? localOptimisticStyles.padding ?? 0}px`;
            
            const paddingBottom = isMobileLayout
                ? `${localOptimisticStyles.paddingBottomMobile ?? localOptimisticStyles.paddingMobile ?? localOptimisticStyles.paddingBottom ?? localOptimisticStyles.padding ?? 0}px`
                : `${localOptimisticStyles.paddingBottom ?? localOptimisticStyles.padding ?? 0}px`;
            
            const paddingLeft = isMobileLayout
                ? `${localOptimisticStyles.paddingLeftMobile ?? localOptimisticStyles.paddingMobile ?? localOptimisticStyles.paddingLeft ?? localOptimisticStyles.padding ?? 0}px`
                : `${localOptimisticStyles.paddingLeft ?? localOptimisticStyles.padding ?? 0}px`;
            
            // ✅ Direktes Style Assignment (wie TipTap - smooth & instant!)
            gridWrapperRef.current.style.maxWidth = maxWidth;
            gridWrapperRef.current.style.marginTop = marginTop;
            gridWrapperRef.current.style.marginBottom = marginBottom;
            gridWrapperRef.current.style.marginLeft = marginLeft;
            gridWrapperRef.current.style.marginRight = marginRight;
            gridWrapperRef.current.style.paddingTop = paddingTop;
            gridWrapperRef.current.style.paddingRight = paddingRight;
            gridWrapperRef.current.style.paddingBottom = paddingBottom;
            gridWrapperRef.current.style.paddingLeft = paddingLeft;
            
            // ✅ Logging für Debug
            console.log('🎯 [GRID SPACING] Applied:', { 
                margins: { marginTop, marginBottom, marginLeft, marginRight },
                paddings: { paddingTop, paddingRight, paddingBottom, paddingLeft },
                isMobileLayout,
                localOptimisticStyles 
            });
        }
    }, [
        // ✅ NUR localOptimisticStyles Dependencies!
        // → KEIN element in dependencies = KEIN Re-Run bei Firebase Updates
        // → Nur User-Actions (Slider bewegen) triggern DOM Updates
        // → Firebase Updates bleiben im Hintergrund, ändern NICHT das UI
        localOptimisticStyles.maxWidth, 
        localOptimisticStyles.maxWidthMobile,
        localOptimisticStyles.marginTop, 
        localOptimisticStyles.marginTopMobile,
        localOptimisticStyles.marginBottom, 
        localOptimisticStyles.marginBottomMobile,
        localOptimisticStyles.marginLeft, 
        localOptimisticStyles.marginLeftMobile,
        localOptimisticStyles.marginRight, 
        localOptimisticStyles.marginRightMobile,
        // ✅ Padding Dependencies
        localOptimisticStyles.padding,
        localOptimisticStyles.paddingMobile,
        localOptimisticStyles.paddingTop,
        localOptimisticStyles.paddingRight,
        localOptimisticStyles.paddingBottom,
        localOptimisticStyles.paddingLeft,
        localOptimisticStyles.paddingTopMobile,
        localOptimisticStyles.paddingRightMobile,
        localOptimisticStyles.paddingBottomMobile,
        localOptimisticStyles.paddingLeftMobile,
        isMobileLayout
        // ❌ KEIN element! Das würde Springen verursachen!
    ]);
    
    // Grid Styles (ohne Border/Background/Margins - die sind im Wrapper!)
    const gridStylesClean = {
        display: 'grid',
        // ✅ Direction Support - respektiert Desktop/Mobile Settings
        ...(gridFlowMode === 'auto-fit' ? {
            // Auto-Fit Mode: gridAutoFlow NICHT setzen, auto-fit handelt das Wrapping
            gridTemplateColumns: getGridTemplateColumns(),
            gridTemplateRows: getGridTemplateRows(),
        } : {
            // Fixed Mode: gridAutoFlow für Direction verwenden
            ...(gridDirection === 'vertical' ? {
                // ✅ FIXED: Vertical = Untereinander
                // → gridAutoFlow: 'row' (Items fließen nach unten)
                // → gridTemplateColumns: '1fr' (NUR eine Spalte - alles untereinander!)
                // → gridTemplateRows: 'auto' (Reihen passen sich an)
                gridAutoFlow: 'row',
                gridTemplateColumns: '1fr',
                gridTemplateRows: 'auto'
            } : {
                gridAutoFlow: 'row', // ✅ Automatisches Wrapping bei zu vielen Cells
                gridTemplateColumns: getGridTemplateColumns(),
                gridTemplateRows: 'auto'
            })
        }),
        // ⚡ OPTIMISTIC GAP: Aus localOptimisticStyles für instant feedback
        ...(gridFlowMode === 'auto-fit' ? {
            rowGap: `${gap}px`,
            columnGap: `${gap}px`
        } : {
            gap: `${gap}px`
        }),
        width: '100%',
        boxSizing: 'border-box', // ✅ Padding/Border in width einrechnen
        // ❌ ENTFERNT: marginTop/Bottom - das sind WRAPPER Margins, nicht Grid Margins!
        // ❌ ENTFERNT: padding - gehört auch nicht hierher
        // ✅ Margin/Padding werden via useEffect auf gridWrapperRef gesetzt!
    };
    
    return (
        <>
            <div 
                ref={gridWrapperRef}
                className={`layout-grid-wrapper ${isSeamlessMode ? 'seamless-mode' : ''}`}
                style={{
                    ...wrapperStyles,
                    '--grid-columns': element.columns || 2 // ✅ CSS Variable für Grid Columns
                }}

                onMouseEnter={() => {
                    // Clear any pending hide timeout
                    if (hideColumnButtonTimeout.current) {
                        clearTimeout(hideColumnButtonTimeout.current);
                        hideColumnButtonTimeout.current = null;
                    }
                    setShowAddColumn(true);
                    setIsGridHovered(true);
                }}
                onMouseLeave={() => {
                    // Delay hiding to allow hovering the button
                    hideColumnButtonTimeout.current = setTimeout(() => {
                        setShowAddColumn(false);
                        setIsGridHovered(false);
                    }, 300);
                }}
            >
                {/* 🎨 Grid Controls - Immer gerendert, aber versteckt wenn Child aktiv */}
                {isEditMode && depth < 2 && (
                    <GridCellControls
                        onEdit={() => setIsGridToolbarVisible(!isGridToolbarVisible)}
                        onDelete={onDelete} // ✅ Modal wird von GridCellControls gehandhabt
                        onDuplicate={onDuplicate} // ✅ Grid Duplicate aktiviert!
                        elementType="grid"
                        canDelete={true}
                        showEdit={true}
                        showDuplicate={!!onDuplicate} // ✅ Zeige Button nur wenn Callback vorhanden
                        showDelete={true}
                        forceHidden={currentlyEditingElementId !== null || hoveredCell !== null}
                    />
                )}
                
                <div className="layout-grid-element" style={gridStylesClean}>
                {children.map((cell, index) => {
                    // ✅ Apply Cell Default Styles from Grid (can be overridden by individual cell)
                    
                    // 🎨 GRADIENT CHECK - Gradient hat Priorität
                    const cellGradientEnabled = (cell.gradientEnabled !== undefined && cell.gradientEnabled !== null)
                        ? cell.gradientEnabled
                        : (element.cellDefaultGradientEnabled || false);
                    
                    const cellGradientColor1 = (cell.gradientColor1 !== undefined && cell.gradientColor1 !== null)
                        ? cell.gradientColor1
                        : (element.cellDefaultGradientColor1 || '#667eea');
                    
                    const cellGradientColor2 = (cell.gradientColor2 !== undefined && cell.gradientColor2 !== null)
                        ? cell.gradientColor2
                        : (element.cellDefaultGradientColor2 || '#764ba2');
                    
                    // 🎨 BACKGROUND - Gradient oder Solid Color
                    // ✅ FIX: cellDefaultBackground wird jetzt korrekt angewendet!
                    const cellBackgroundColor = (cell.backgroundColor !== undefined && cell.backgroundColor !== null)
                        ? cell.backgroundColor
                        : (element.cellDefaultBackground || 'transparent');
                    
                    // 🎨 OPACITY - Nur für Background, nicht für die ganze Cell!
                    // ⚡ OPTIMISTIC: Cell Opacity aus localOptimisticStyles
                    const cellOpacity = (cell.opacity !== undefined && cell.opacity !== null)
                        ? cell.opacity / 100
                        : ((localOptimisticStyles.cellDefaultOpacity ?? element.cellDefaultOpacity ?? 100) / 100);
                    
                    // ✅ Helper: Convert hex/named color to rgba with opacity
                    const applyOpacityToColor = (color, opacity) => {
                        if (!color || color === 'transparent') return 'transparent';
                        
                        // Check if already rgba
                        if (color.startsWith('rgba')) {
                            return color.replace(/[\d.]+\)$/g, `${opacity})`);
                        }
                        
                        // Check if rgb
                        if (color.startsWith('rgb')) {
                            return color.replace('rgb', 'rgba').replace(')', `, ${opacity})`);
                        }
                        
                        // Hex color - convert to rgba
                        if (color.startsWith('#')) {
                            const hex = color.replace('#', '');
                            const r = parseInt(hex.substring(0, 2), 16);
                            const g = parseInt(hex.substring(2, 4), 16);
                            const b = parseInt(hex.substring(4, 6), 16);
                            return `rgba(${r}, ${g}, ${b}, ${opacity})`;
                        }
                        
                        // Named colors - wrap in rgba with opacity using CSS color-mix
                        // Fallback: return color as-is
                        return color;
                    };
                    
                    // ✅ Apply opacity to background (not to entire cell!)
                    const cellBackground = cellGradientEnabled
                        ? `linear-gradient(135deg, ${applyOpacityToColor(cellGradientColor1, cellOpacity)} 0%, ${applyOpacityToColor(cellGradientColor2, cellOpacity)} 100%)`
                        : applyOpacityToColor(cellBackgroundColor, cellOpacity);
                    
                    // 🎨 BACKDROP BLUR
                    const cellBackdropBlur = (cell.backdropBlur !== undefined && cell.backdropBlur !== null)
                        ? cell.backdropBlur
                        : (element.cellDefaultBackdropBlur || 'none');
                    
                    const getBackdropBlurValue = (blur) => {
                        const blurMap = {
                            'light': 'blur(4px)',
                            'medium': 'blur(8px)',
                            'strong': 'blur(16px)',
                            'none': 'none'
                        };
                        return blurMap[blur] || 'none';
                    };
                    
                    // 🎨 BOX SHADOW
                    const cellBoxShadow = (cell.boxShadow !== undefined && cell.boxShadow !== null)
                        ? cell.boxShadow
                        : (element.cellDefaultBoxShadow || 'none');
                    
                    const getBoxShadowValue = (shadow) => {
                        const shadowMap = {
                            'small': '0 2px 4px rgba(0, 0, 0, 0.1)',
                            'medium': '0 4px 12px rgba(0, 0, 0, 0.15)',
                            'large': '0 8px 24px rgba(0, 0, 0, 0.2)',
                            'none': 'none'
                        };
                        return shadowMap[shadow] || 'none';
                    };
                    
                    // ⚡ OPTIMISTIC: Cell Padding aus localOptimisticStyles (Individual sides)
                    // ✅ Desktop Padding
                    const cellPaddingTop = (cell.paddingTop !== undefined && cell.paddingTop !== null)
                        ? cell.paddingTop 
                        : (localOptimisticStyles.cellDefaultPaddingTop ?? element.cellDefaultPaddingTop ?? element.cellDefaultPadding ?? 15);
                    
                    const cellPaddingRight = (cell.paddingRight !== undefined && cell.paddingRight !== null)
                        ? cell.paddingRight 
                        : (localOptimisticStyles.cellDefaultPaddingRight ?? element.cellDefaultPaddingRight ?? element.cellDefaultPadding ?? 15);
                    
                    const cellPaddingBottom = (cell.paddingBottom !== undefined && cell.paddingBottom !== null)
                        ? cell.paddingBottom 
                        : (localOptimisticStyles.cellDefaultPaddingBottom ?? element.cellDefaultPaddingBottom ?? element.cellDefaultPadding ?? 15);
                    
                    const cellPaddingLeft = (cell.paddingLeft !== undefined && cell.paddingLeft !== null)
                        ? cell.paddingLeft 
                        : (localOptimisticStyles.cellDefaultPaddingLeft ?? element.cellDefaultPaddingLeft ?? element.cellDefaultPadding ?? 15);
                    
                    // ✅ Mobile Padding
                    const cellPaddingTopMobile = (cell.paddingTopMobile !== undefined && cell.paddingTopMobile !== null)
                        ? cell.paddingTopMobile 
                        : (localOptimisticStyles.cellDefaultPaddingTopMobile ?? element.cellDefaultPaddingTopMobile ?? element.cellDefaultPaddingMobile ?? cellPaddingTop);
                    
                    const cellPaddingRightMobile = (cell.paddingRightMobile !== undefined && cell.paddingRightMobile !== null)
                        ? cell.paddingRightMobile 
                        : (localOptimisticStyles.cellDefaultPaddingRightMobile ?? element.cellDefaultPaddingRightMobile ?? element.cellDefaultPaddingMobile ?? cellPaddingRight);
                    
                    const cellPaddingBottomMobile = (cell.paddingBottomMobile !== undefined && cell.paddingBottomMobile !== null)
                        ? cell.paddingBottomMobile 
                        : (localOptimisticStyles.cellDefaultPaddingBottomMobile ?? element.cellDefaultPaddingBottomMobile ?? element.cellDefaultPaddingMobile ?? cellPaddingBottom);
                    
                    const cellPaddingLeftMobile = (cell.paddingLeftMobile !== undefined && cell.paddingLeftMobile !== null)
                        ? cell.paddingLeftMobile 
                        : (localOptimisticStyles.cellDefaultPaddingLeftMobile ?? element.cellDefaultPaddingLeftMobile ?? element.cellDefaultPaddingMobile ?? cellPaddingLeft);
                    
                    // ⚡ OPTIMISTIC: Cell Border Radius aus localOptimisticStyles
                    const cellBorderRadius = (cell.borderRadius !== undefined && cell.borderRadius !== null)
                        ? cell.borderRadius 
                        : (localOptimisticStyles.cellDefaultBorderRadius ?? element.cellDefaultBorderRadius ?? 8);
                    
                    const cellBorderEnabled = (cell.borderEnabled !== undefined && cell.borderEnabled !== null)
                        ? cell.borderEnabled 
                        : (element.cellDefaultBorderEnabled || false);
                    
                    // ⚡ OPTIMISTIC: Cell Border Width aus localOptimisticStyles
                    const cellBorderWidth = (cell.borderWidth !== undefined && cell.borderWidth !== null)
                        ? cell.borderWidth 
                        : (localOptimisticStyles.cellDefaultBorderWidth ?? element.cellDefaultBorderWidth ?? 1);
                    
                    const cellBorderColor = (cell.borderColor !== undefined && cell.borderColor !== null)
                        ? cell.borderColor 
                        : (element.cellDefaultBorderColor || '#e0e0e0');
                    
                    // ✅ SEAMLESS MODE - Border Logic (no double borders!)
                    const isSeamlessMode = element.seamlessMode || false;
                    const seamlessBorderWidth = element.seamlessBorderWidth || 1;
                    const seamlessBorderColor = element.seamlessBorderColor || '#e0e0e0';
                    const seamlessBorderStyle = element.seamlessBorderStyle || 'solid';
                    
                    // Calculate which cell is in last column/row
                    const isHorizontal = gridDirection === 'horizontal';
                    const isLastColumn = isHorizontal && ((index + 1) % element.columns === 0);
                    const isLastRow = isHorizontal && (index >= children.length - element.columns);
                    const isLastCell = index === children.length - 1;
                    
                    // ✅ Seamless border construction - separate properties!
                    let cellBorderTop = 'none';
                    let cellBorderRight = 'none';
                    let cellBorderBottom = 'none';
                    let cellBorderLeft = 'none';
                    
                    if (isSeamlessMode) {
                        const borderValue = `${seamlessBorderWidth}px ${seamlessBorderStyle} ${seamlessBorderColor}`;
                        
                        if (isHorizontal) {
                            // Horizontal Grid: border-right für alle außer letzte Spalte
                            if (!isLastColumn) {
                                cellBorderRight = borderValue;
                            }
                            
                            // border-bottom für alle außer letzte Reihe
                            if (!isLastRow) {
                                cellBorderBottom = borderValue;
                            }
                        } else {
                            // Vertical Grid: border-bottom nur für alle außer letztes Element
                            if (!isLastCell) {
                                cellBorderBottom = borderValue;
                            }
                        }
                    } else if (cellBorderEnabled) {
                        // Regular border mode - alle Seiten
                        const borderValue = `${cellBorderWidth}px solid ${cellBorderColor}`;
                        cellBorderTop = borderValue;
                        cellBorderRight = borderValue;
                        cellBorderBottom = borderValue;
                        cellBorderLeft = borderValue;
                    }
                    
                    // ⚡ OPTIMISTIC: Cell Height aus localOptimisticStyles
                    const cellHeight = element.cellDefaultFixedHeight 
                        ? `${localOptimisticStyles.cellDefaultHeight ?? element.cellDefaultHeight ?? 200}px` 
                        : (cell.height || 'auto');
                    
                    // ✅ Cell Min/Max Width (Desktop/Mobile) - NUR im auto-fit mode
                    const cellMinWidth = gridFlowMode === 'auto-fit' 
                        ? (isMobileLayout
                            ? (element.cellDefaultMinWidthMobile ?? element.cellDefaultMinWidth)
                            : element.cellDefaultMinWidth)
                        : undefined; // ✅ In fixed mode: keine min-width (Grid übernimmt)
                    
                    const cellMaxWidth = isMobileLayout
                        ? (element.cellDefaultMaxWidthMobile ?? element.cellDefaultMaxWidth)
                        : element.cellDefaultMaxWidth;
                    
                    // ✅ Cell Content Flex Direction (Desktop/Mobile)
                    const cellFlexDirection = isMobileLayout
                        ? (element.cellDefaultFlexDirectionMobile ?? element.cellDefaultFlexDirection ?? 'column')
                        : (element.cellDefaultFlexDirection ?? 'column');
                    
                    return (
                <div
                    key={cell.id}
                    className={`grid-cell ${hoveredCell === index ? 'hovered' : ''} ${(element.cellDefaultOverrideNestedBackgrounds !== false && cellBackground !== 'transparent') ? 'override-nested-bg' : ''}`}
                    style={{
                        background: cellBackground, // ✅ Background auf Cell (wie vorher)
                        backdropFilter: getBackdropBlurValue(cellBackdropBlur),
                        WebkitBackdropFilter: getBackdropBlurValue(cellBackdropBlur), // Safari support
                        boxShadow: getBoxShadowValue(cellBoxShadow),
                        // ✅ CSS Variable für verschachtelte Elemente (Override Nested Backgrounds)
                        '--cell-background': cellBackground,
                        // ✅ WICHTIG: Parent Cell Background als separate Variable für tiefe Vererbung
                        '--parent-cell-background': cellBackground,
                        // ✅ Individual Padding (Top, Right, Bottom, Left)
                        paddingTop: isMobileLayout ? `${cellPaddingTopMobile}px` : `${cellPaddingTop}px`,
                        paddingRight: isMobileLayout ? `${cellPaddingRightMobile}px` : `${cellPaddingRight}px`,
                        paddingBottom: isMobileLayout ? `${cellPaddingBottomMobile}px` : `${cellPaddingBottom}px`,
                        paddingLeft: isMobileLayout ? `${cellPaddingLeftMobile}px` : `${cellPaddingLeft}px`,
                        borderRadius: isSeamlessMode ? 0 : `${cellBorderRadius}px`, // ✅ No radius in seamless mode
                        // ✅ Separate border properties for seamless mode
                        borderTop: cellBorderTop,
                        borderRight: cellBorderRight,
                        borderBottom: cellBorderBottom,
                        borderLeft: cellBorderLeft,
                        // ✅ min-height nur bei leeren Cells ODER wenn Fixed Height aktiviert
                        minHeight: cellHeight !== 'auto' 
                            ? cellHeight 
                            : (cell.children && cell.children.length > 0 ? 'auto' : '100px'),
                        height: cellHeight !== 'auto' ? cellHeight : 'auto',
                        // ✅ Cell Width Constraints - nur max-width, kein min-width in fixed mode
                        maxWidth: cellMaxWidth ? `${cellMaxWidth}px` : undefined,
                        minWidth: 0, // ✅ CRITICAL: Verhindert dass Cells zu breit werden
                        position: 'relative',
                        // ✅ Cell ist nur Container, Alignment wird in grid-cell-content gehandhabt
                        display: 'flex',
                        flexDirection: 'column',
                        boxSizing: 'border-box' // ✅ Padding/Border in width einrechnen
                    }}
                    onMouseEnter={() => setHoveredCell(index)}
                    onMouseLeave={() => setHoveredCell(null)}
                >
                    {/* Cell Content - Recursive rendering */}
                    {cell.children && cell.children.length > 0 ? (
                        <div 
                            className="grid-cell-content"
                            style={{
                                width: '100%',
                                display: 'flex',
                                // ✅ Cell Content Direction (Desktop/Mobile responsive)
                                flexDirection: cellFlexDirection,
                                // ✅ Cell Content Alignment (aus Cell Layout Settings)
                                alignItems: element.cellDefaultAlignItems || 'flex-start',
                                justifyContent: element.cellDefaultJustifyContent || 'center',
                                // ✅ Gap between elements (responsive)
                                gap: isMobileLayout
                                    ? `${element.cellDefaultGapMobile ?? element.cellDefaultGap ?? 12}px`
                                    : `${element.cellDefaultGap ?? 12}px`
                            }}
                        >
                            {cell.children.map((child) => {
                                // ✅ Use renderChild function if provided (recursive)
                                if (renderChild) {
                                    return renderChild(child, cell.id);
                                }
                                return null;
                            })}
                        </div>
                    ) : (
                        // ✅ Empty cell state with Add Menu
                        isEditMode && (
                            <div className="grid-cell-empty">
                                {activeCellMenu !== cell.id ? (
                                    <button 
                                        ref={cellMenuButtonRef}
                                        className="cell-add-content-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            // Clear any pending close timeout
                                            if (cellMenuCloseTimeoutRef.current) {
                                                clearTimeout(cellMenuCloseTimeoutRef.current);
                                                cellMenuCloseTimeoutRef.current = null;
                                            }
                                            setActiveCellMenu(cell.id);
                                        }}
                                        onMouseEnter={() => {
                                            // Keep menu open when hovering button
                                            if (cellMenuCloseTimeoutRef.current) {
                                                clearTimeout(cellMenuCloseTimeoutRef.current);
                                                cellMenuCloseTimeoutRef.current = null;
                                            }
                                        }}
                                    >
                                        <i className="fas fa-plus"></i>
                                        <span>Add Content</span>
                                    </button>
                                ) : (
                                    <div 
                                        ref={cellMenuRef}
                                        onMouseEnter={() => {
                                            // Keep menu open when hovering it
                                            if (cellMenuCloseTimeoutRef.current) {
                                                clearTimeout(cellMenuCloseTimeoutRef.current);
                                                cellMenuCloseTimeoutRef.current = null;
                                            }
                                        }}
                                    >
                                        <AddElementMenu
                                            onAdd={(type, elementType) => handleAddToCell(cell.id, type, elementType)}
                                            onClose={() => setActiveCellMenu(null)}
                                            colors={colors}
                                        />
                                    </div>
                                )}
                            </div>
                        )
                    )}
                    
                    {/* 🎨 Cell Controls - Immer gerendert, aber versteckt wenn Element bearbeitet */}
                    {isEditMode && depth < 2 && (
                        <GridCellControls
                            onEdit={() => setIsGridToolbarVisible(!isGridToolbarVisible)}
                            onDelete={() => {
                                const cellIndex = children.findIndex(c => c.id === cell.id);
                                setCellToDelete({ id: cell.id, index: cellIndex + 1 });
                                setIsDeleteModalOpen(true);
                            }}
                            onDuplicate={() => handleCopyCell(cell.id)}
                            elementType="cell"
                            canDelete={children.length > 1}
                            showEdit={true}
                            showDuplicate={true}
                            showDelete={true}
                            forceHidden={currentlyEditingElementId !== null}
                        />
                    )}
                </div>
            );
            })}
            
            {/* ✅ DEZENTER Add Column Button - Nur beim Grid Hover */}
            {isEditMode && !isMobileLayout && children.length < 6 && showAddColumn && (
                <div 
                    className="grid-add-column-trigger"
                    onClick={handleAddColumn}
                    onMouseEnter={() => {
                        // Keep button visible when hovering it
                        if (hideColumnButtonTimeout.current) {
                            clearTimeout(hideColumnButtonTimeout.current);
                            hideColumnButtonTimeout.current = null;
                        }
                        setShowAddColumn(true);
                    }}
                    onMouseLeave={() => {
                        // Hide after delay when leaving button
                        hideColumnButtonTimeout.current = setTimeout(() => {
                            setShowAddColumn(false);
                        }, 100);
                    }}
                    title="Add Column"
                >
                    <i className="fas fa-plus"></i>
                </div>
            )}
            </div>
        </div>
            
            {/* 🗑️ Delete Cell Modal - Rendered via Portal */}
            {isDeleteModalOpen && cellToDelete && ReactDOM.createPortal(
                <>
                    {/* Backdrop */}
                    <div 
                        className="delete-modal-backdrop"
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'rgba(0, 0, 0, 0.5)',
                            zIndex: 999998,
                            backdropFilter: 'blur(4px)'
                        }}
                        onClick={() => {
                            setIsDeleteModalOpen(false);
                            setCellToDelete(null);
                        }}
                    />
                    {/* Modal */}
                    <div className="delete_modal" style={{ zIndex: 999999 }}>
                        <p>Delete Cell {cellToDelete.index}?</p>
                        <button 
                            onClick={() => {
                                handleDeleteCell(cellToDelete.id);
                                setIsDeleteModalOpen(false);
                                setCellToDelete(null);
                            }}
                        >
                            Yes
                        </button>
                        <button 
                            onClick={() => {
                                setIsDeleteModalOpen(false);
                                setCellToDelete(null);
                            }}
                        >
                            No
                        </button>
                    </div>
                </>,
                document.body
            )}
            
            
            {/* ✅ Grid Floating Toolbar */}
            <GridFloatingToolbar
                isEditMode={isEditMode}
                isVisible={isGridToolbarVisible}
                gridElement={{
                    ...element,
                    ...localOptimisticStyles // ⚡ OPTIMISTIC MERGE! Wie ContainerFloatingToolbar!
                }}
                onStyleChange={(updates) => {
                    // console.log('⚡ [Grid Toolbar] Style Update (instant):', updates);
                    
                    // ⚡ STEP 1: SOFORT lokalen State updaten für instant UI feedback
                    // → Triggert useEffect der DOM manipuliert
                    // → Kein Sync zurück von Firebase = Kein Springen!
                    setLocalOptimisticStyles(prev => ({
                        ...prev,
                        ...updates
                    }));
                    
                    // ⚡ STEP 2: DEBOUNCED Firebase Update nach 300ms
                    const updatedElement = { ...element, ...updates };
                    debouncedParentUpdate(updatedElement);
                    
                    // ✅ Das war's! Kein pending tracking nötig.
                    // Firebase Update ändert element prop, aber:
                    // → localOptimisticStyles wird NICHT mehr mit element gesynct
                    // → localOptimisticStyles bleibt bei User-Werten
                    // → Kein Springen! 🎯
                }}
                onClose={() => setIsGridToolbarVisible(false)}
                gridRef={gridWrapperRef}
                isMobileLayout={isMobileLayout}
                colors={colors} // ✅ Brand Colors durchreichen
            />
        </>
    );
}

export default LayoutGridElement;

